// src/components/IncomeTaxPage.js
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import SkeletonLoader from './SkeletonLoader';
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import api from '../services/api';
import toast from 'react-hot-toast';



// ─── Constants ────────────────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();

const DEDUCTION_LABELS = {
  MEDICAL: '🏥 Despesas Médicas',
  EDUCATION: '🎓 Educação',
  PENSION_PRIVATE: '💼 Previdência Privada (PGBL)',
  INSS: '🛡️ INSS (avulso/facultativo)',
  OTHER: '📄 Outras Deduções',
};

const INCOME_TYPE_LABELS = {
  SALARY_CLT: 'Salário CLT',
  SALARY_PJ: 'Pró-labore / PJ',
  FREELANCE: 'Autônomo / Freela',
  RENTAL: 'Aluguel',
  INVESTMENT: 'Rendimento Financeiro',
  MEI: 'MEI',
  OTHER: 'Outros',
};

function fmt(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function IncomeTaxPage() {
  const [activeTab, setActiveTab] = useState('summary');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(true);

  // Data
  const [summary, setSummary] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [config, setConfig] = useState({ dependents: 0, regime: 'COMPLETE', is_mei: false });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDed, setEditingDed] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, dedRes, cfgRes] = await Promise.all([
        api.get(`/ir/summary?year=${year}`),
        api.get(`/ir/deductions?year=${year}`),
        api.get(`/ir/config?year=${year}`),
      ]);
      setSummary(sumRes.data);
      setDeductions(dedRes.data);
      setConfig(cfgRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados do IR.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Deduction CRUD ──────────────────────────────────────────────────────────
  const handleSaveDed = async (formData) => {
    try {
      if (editingDed) {
        await api.put(`/ir/deductions/${editingDed.id}`, formData);
        toast.success('Dedução atualizada!');
      } else {
        await api.post('/ir/deductions', { ...formData, year });
        toast.success('Dedução adicionada!');
      }
      setModalOpen(false);
      setEditingDed(null);
      fetchAll();
    } catch {
      toast.error('Erro ao salvar dedução.');
    }
  };

  const handleDeleteDed = async (id) => {
    if (!window.confirm('Remover esta dedução?')) return;
    try {
      await api.delete(`/ir/deductions/${id}`);
      toast.success('Dedução removida.');
      fetchAll();
    } catch {
      toast.error('Erro ao remover dedução.');
    }
  };

  // ── Config ──────────────────────────────────────────────────────────────────
  const handleSaveConfig = async () => {
    try {
      await api.put('/ir/config', { ...config, year });
      toast.success('Configurações salvas!');
      fetchAll();
    } catch {
      toast.error('Erro ao salvar configurações.');
    }
  };

  const handleSaveConfigChange = async (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    try {
      await api.put('/ir/config', { ...newConfig, year });
      toast.success('Configuração alterada');
      fetchAll();
    } catch {
      toast.error('Erro ao salvar.');
    }
  };

  // ── Situação ────────────────────────────────────────────────────────────────
  const situacaoInfo = summary ? (
    summary.situacao === 'A PAGAR'
      ? { label: 'Imposto a Pagar', icon: FiAlertCircle, color: '#ff5b5b', bg: 'rgba(255,91,91,0.12)' }
      : summary.situacao === 'A RESTITUIR'
        ? { label: 'A Restituir', icon: FiCheckCircle, color: '#00f5c3', bg: 'rgba(0,245,195,0.12)' }
        : { label: 'Zerado', icon: FiCheckCircle, color: '#0088FE', bg: 'rgba(0,136,254,0.12)' }
  ) : null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
        <div>
          <h2 className="text-4xl font-bold font-manrope text-slate-900 tracking-tight mb-4">Imposto de Renda</h2>
          <div className="flex items-center space-x-3 text-sm text-slate-500 font-medium">
            <div className="flex items-center bg-slate-100 px-3 py-1.5 rounded-lg text-slate-700">
              <span className="material-symbols-outlined text-[18px] mr-2">calendar_today</span>
              Ano-Calendário {year}
            </div>
            <span className="text-slate-300">•</span>
            <span>Declaração de Ajuste Anual IRPF</span>
          </div>
        </div>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-lg px-4 py-2 outline-none hover:bg-slate-50 cursor-pointer shadow-sm"
        >
          {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <div className="flex space-x-8 border-b border-slate-200 mb-8">
        {[
          { id: 'summary', label: 'Resumo Anual' },
          { id: 'deductions', label: 'Deduções' },
          { id: 'config', label: 'Configurações' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-4 text-sm font-bold transition-colors relative ${activeTab === tab.id ? 'text-primary' : 'text-slate-500 hover:text-slate-800'}`}
          >
            {tab.label}
            {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full"></div>}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonLoader variant="ir-summary" />
      ) : (
        <>
          {activeTab === 'summary' && summary && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm">
                  <div className="flex justify-between items-start mb-6">
                    <p className="text-xs font-bold text-slate-500 tracking-widest uppercase">Rendimentos Brutos</p>
                    <span className="material-symbols-outlined text-primary">account_balance</span>
                  </div>
                  <h3 className="text-3xl font-bold font-manrope text-slate-900 mb-2">{fmt(summary.receita_bruta_anual)}</h3>
                  <span className="text-xs font-bold text-green-500 flex items-center"><span className="material-symbols-outlined text-sm mr-1">trending_up</span> +4.2% vs {year - 1}</span>
                </div>

                <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm">
                  <div className="flex justify-between items-start mb-6">
                    <p className="text-xs font-bold text-slate-500 tracking-widest uppercase">Total de Deduções</p>
                    <span className="material-symbols-outlined text-primary">receipt_long</span>
                  </div>
                  <h3 className="text-3xl font-bold font-manrope text-slate-900 mb-2">{fmt(summary.total_deducoes)}</h3>
                  <span className="text-xs font-semibold text-slate-500">Regime {summary.regime === 'COMPLETE' ? 'Completo' : 'Simplificado'}</span>
                </div>

                <div className="bg-primary p-8 rounded-xl shadow-md text-white flex flex-col justify-between">
                  <div className="flex justify-between items-start mb-6">
                    <p className="text-xs font-bold text-indigo-200 tracking-widest uppercase">Situação Atual</p>
                    <span className="px-2 py-0.5 bg-indigo-500/50 rounded text-[10px] font-bold tracking-widest uppercase border border-indigo-400">Calculado</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-indigo-100 mb-1">{summary.situacao === 'A PAGAR' ? 'Valor a Pagar' : summary.situacao === 'A RESTITUIR' ? 'Valor a Restituir' : 'Imposto Zerado'}</p>
                    <h3 className="text-4xl font-bold font-manrope mb-6">{fmt(Math.abs(summary.saldo_ir))}</h3>
                    <button className="w-full py-2.5 bg-white text-primary rounded-lg text-sm font-bold flex items-center justify-center space-x-2 hover:bg-slate-50 transition-colors">
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      <span>Gerar Relatório Completo</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm p-8">
                  <div className="flex justify-between items-center mb-8">
                    <h3 className="text-xl font-bold font-manrope text-slate-900">Evolução Mensal</h3>
                    <div className="flex space-x-4">
                      <span className="flex items-center text-xs font-bold text-slate-500"><span className="w-3 h-3 bg-primary rounded-sm mr-2"></span> Receita</span>
                      <span className="flex items-center text-xs font-bold text-slate-500"><span className="w-3 h-3 bg-indigo-200 rounded-sm mr-2"></span> IR Retido</span>
                    </div>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={summary.detalhamento_mensal} margin={{ top: 0, right: 0, left: -20, bottom: 0 }} barSize={16}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="mes_label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10, fontWeight: 'bold' }} />
                        <YAxis axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fill: '#64748b', fontSize: 11 }} />
                        <Tooltip formatter={(value) => fmt(value)} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} cursor={{ fill: '#f8fafc' }} />
                        <Bar dataKey="receita_tributavel" name="Receita" fill="#3525cd" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="ir_retido" name="IR Retido" fill="#c7d2fe" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="lg:col-span-1 flex flex-col gap-4">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-500 tracking-widest uppercase mb-1">Base de Cálculo</p>
                      <p className="text-xl font-bold font-manrope text-slate-900">{fmt(summary.base_calculo_anual)}</p>
                    </div>
                    <div className="text-slate-400"><span className="material-symbols-outlined text-3xl">calculate</span></div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-500 tracking-widest uppercase mb-1">Imposto Apurado</p>
                      <p className="text-xl font-bold font-manrope text-slate-900">{fmt(summary.ir_calculado_anual)}</p>
                    </div>
                    <div className="text-slate-400"><span className="material-symbols-outlined text-3xl">account_balance_wallet</span></div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-slate-500 tracking-widest uppercase mb-1">Imposto Retido</p>
                      <p className="text-xl font-bold font-manrope text-slate-900">{fmt(summary.ir_retido_total)}</p>
                    </div>
                    <div className="text-slate-400"><span className="material-symbols-outlined text-3xl">pin_drop</span></div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex justify-between items-end mb-6">
                  <h3 className="text-xl font-bold font-manrope text-slate-900">Principais Deduções</h3>
                  <button className="text-sm font-bold text-primary hover:text-indigo-800 transition-colors flex items-center" onClick={() => setActiveTab('deductions')}>Ver todas</button>
                </div>

                <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full text-left min-w-[480px]">
                    <thead>
                      <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200">
                        <th className="px-6 py-4">Categoria</th>
                        <th className="px-6 py-4">Descrição</th>
                        <th className="px-6 py-4 text-right">Valor (R$)</th>
                        <th className="px-6 py-4"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-slate-50">
                      {deductions.length > 0 ? deductions.slice(0, 3).map(ded => (
                        <tr key={ded.id} className="hover:bg-slate-100 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded bg-white shadow-sm flex items-center justify-center text-slate-500 text-sm">
                                {ded.type === 'MEDICAL' ? '🏥' : ded.type === 'EDUCATION' ? '🎓' : ded.type === 'DEPENDENT' ? '👨‍👩‍👧' : '📄'}
                              </div>
                              <span className="font-bold text-sm text-slate-700">{DEDUCTION_LABELS[ded.type] || ded.type}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-600">{ded.description || 'Nenhuma descrição fornecida'}</td>
                          <td className="px-6 py-4 text-right font-bold text-slate-900">{fmt(ded.amount)}</td>
                          <td className="px-6 py-4 text-right text-slate-400"><button onClick={() => { setEditingDed(ded); setModalOpen(true); }}><span className="material-symbols-outlined text-[18px]">edit</span></button></td>
                        </tr>
                      )) : (
                        <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-500 font-medium">Nenhuma dedução encontrada.</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-8 flex flex-col md:flex-row gap-8 justify-between items-start mt-8">
                <div className="md:w-1/3">
                  <h4 className="text-lg font-bold font-manrope text-slate-900 mb-2">Configuração de Declaração</h4>
                  <p className="text-sm font-medium text-slate-500 leading-relaxed">Defina o regime de tributação e status ocupacional para otimizar o cálculo de seu imposto.</p>
                </div>
                <div className="flex-1 flex flex-wrap gap-6 items-end">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Regime Tributário</label>
                    <select value={config.regime} onChange={e => handleSaveConfigChange('regime', e.target.value)} className="bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary w-56">
                      <option value="COMPLETE">Completa (Deduções)</option>
                      <option value="SIMPLIFIED">Simplificada (Padrão)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Dependentes</label>
                    <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-hidden h-[46px] w-32">
                      <button className="flex-1 flex items-center justify-center text-primary font-bold hover:bg-slate-100" onClick={() => handleSaveConfigChange('dependents', Math.max(0, config.dependents - 1))}>–</button>
                      <input type="text" className="w-12 text-center bg-transparent border-none text-slate-800 font-bold outline-none" value={config.dependents.toString().padStart(2, '0')} readOnly />
                      <button className="flex-1 flex items-center justify-center text-primary font-bold hover:bg-slate-100" onClick={() => handleSaveConfigChange('dependents', config.dependents + 1)}>+</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Status Ocupacional</label>
                    <div className="h-[46px] flex items-center">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <span className="text-sm font-medium text-slate-600">Possui MEI ativo?</span>
                        <div className={`w-12 h-6 rounded-full p-1 transition-colors ${config.is_mei ? 'bg-primary' : 'bg-slate-300'}`} onClick={() => handleSaveConfigChange('is_mei', !config.is_mei)}>
                          <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${config.is_mei ? 'translate-x-6' : 'translate-x-0'}`}></div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'deductions' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="text-xl font-bold font-manrope text-slate-900">Deduções de {year}</h3>
                  <p className="text-sm font-medium text-slate-500 mt-1">Despesas que reduzem a base de cálculo do imposto.</p>
                </div>
                <button onClick={() => { setEditingDed(null); setModalOpen(true); }} className="flex items-center space-x-2 bg-primary text-white px-4 py-2.5 rounded-lg font-bold text-sm shadow-md hover:bg-indigo-700 transition-colors">
                  <span className="material-symbols-outlined text-lg">add</span>
                  <span>Nova Dedução</span>
                </button>
              </div>

              {deductions.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-100 p-16 flex flex-col items-center justify-center text-center">
                  <span className="material-symbols-outlined text-6xl text-slate-200 mb-4">receipt_long</span>
                  <h4 className="text-lg font-bold text-slate-700 mb-2">Nenhuma dedução registrada</h4>
                  <p className="text-sm text-slate-500 mb-6">Adicione despesas médicas, educação, PGBL e mais.</p>
                  <button onClick={() => { setEditingDed(null); setModalOpen(true); }} className="text-primary font-bold text-sm hover:underline">Adicionar a primeira dedução</button>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full text-left min-w-[480px]">
                    <thead>
                      <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 bg-slate-50">
                        <th className="px-6 py-4">Categoria</th>
                        <th className="px-6 py-4">Descrição</th>
                        <th className="px-6 py-4 text-right">Valor (R$)</th>
                        <th className="px-6 py-4 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {deductions.map(ded => (
                        <tr key={ded.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded bg-slate-100 text-slate-500 flex items-center justify-center text-sm">
                                {ded.type === 'MEDICAL' ? '🏥' : ded.type === 'EDUCATION' ? '🎓' : ded.type === 'DEPENDENT' ? '👨‍👩‍👧' : '📄'}
                              </div>
                              <span className="font-bold text-sm text-slate-700">{DEDUCTION_LABELS[ded.type] || ded.type}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-600">{ded.description || '—'}</td>
                          <td className="px-6 py-4 text-right font-bold text-slate-900 text-lg">{fmt(ded.amount)}</td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center space-x-2">
                              <button onClick={() => { setEditingDed(ded); setModalOpen(true); }} className="w-8 h-8 rounded bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors" title="Editar"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                              <button onClick={() => handleDeleteDed(ded.id)} className="w-8 h-8 rounded bg-red-50 text-error flex items-center justify-center hover:bg-red-100 transition-colors" title="Excluir"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  <div className="bg-slate-50 p-6 flex justify-end items-center border-t border-slate-100">
                    <span className="text-sm font-bold text-slate-500 uppercase tracking-widest mr-4">Total Deduções</span>
                    <span className="text-2xl font-bold font-manrope text-primary">{fmt(deductions.reduce((s, d) => s + d.amount, 0))}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'config' && (
            <div className="max-w-2xl bg-white rounded-xl border border-slate-100 shadow-sm p-8">
              <h3 className="text-xl font-bold font-manrope text-slate-900 mb-2">Configurações Fiscais</h3>
              <p className="text-sm font-medium text-slate-500 mb-8">Defina seu perfil tributário para um cálculo mais preciso do seu imposto de renda no ano base de {year}.</p>

              <form className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Regime de Tributação</label>
                  <select value={config.regime} onChange={e => setConfig({ ...config, regime: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary">
                    <option value="COMPLETE">Completo (Deduções Legais — saúde, educação, etc.)</option>
                    <option value="SIMPLIFIED">Simplificado (Desconto padrão de 20%)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Número de Dependentes</label>
                  <input type="number" min="0" value={config.dependents} onChange={e => setConfig({ ...config, dependents: parseInt(e.target.value) || 0 })} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                  <p className="mt-2 text-xs text-slate-400 font-medium">Cada dependente deduz R$ 2.275,08/ano da base de cálculo estrutural.</p>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex items-start space-x-3">
                  <div className="pt-0.5">
                    <input type="checkbox" id="mei" checked={config.is_mei} onChange={e => setConfig({ ...config, is_mei: e.target.checked })} className="w-4 h-4 text-primary rounded border-slate-300 focus:ring-primary" />
                  </div>
                  <div>
                    <label htmlFor="mei" className="text-sm font-bold text-slate-800 block cursor-pointer">Sou MEI (Microempreendedor Individual)</label>
                    <p className="text-xs font-medium text-slate-500 mt-1">Habilita a apuração mista, isentando lucro presumido e tributando pró-labore via carnê-leão.</p>
                  </div>
                </div>

                <button type="button" onClick={handleSaveConfig} className="bg-primary hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-xl text-sm shadow-md transition-colors w-full sm:w-auto">
                  Salvar Configurações
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-slate-100">
                <div className="flex items-center space-x-2 text-primary mb-4">
                  <span className="material-symbols-outlined">info</span>
                  <p className="font-bold text-sm">Tabela Progressiva IRPF 2024</p>
                </div>
                <table className="w-full text-left text-sm text-slate-500">
                  <thead>
                    <tr>
                      <th className="pb-2 font-bold text-slate-700">Base de Cálculo Anual (R$)</th>
                      <th className="pb-2 text-right font-bold text-slate-700">Alíquota</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr><td className="py-2">Até 27.110,40</td><td className="py-2 text-right font-bold text-green-500">Isento</td></tr>
                    <tr><td className="py-2">27.110,41 – 33.919,80</td><td className="py-2 text-right font-bold">7,5%</td></tr>
                    <tr><td className="py-2">33.919,81 – 45.012,60</td><td className="py-2 text-right font-bold">15%</td></tr>
                    <tr><td className="py-2">45.012,61 – 55.976,16</td><td className="py-2 text-right font-bold">22,5%</td></tr>
                    <tr><td className="py-2">Acima de 55.976,16</td><td className="py-2 text-right font-bold">27,5%</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => { setModalOpen(false); setEditingDed(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-lg font-bold font-manrope text-slate-900">{editingDed ? 'Editar Dedução' : 'Nova Dedução'}</h3>
              <button className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors" onClick={() => { setModalOpen(false); setEditingDed(null); }}><span className="material-symbols-outlined text-[20px]">close</span></button>
            </div>
            <div className="p-6">
              <form className="space-y-5" onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const payload = {
                  type: formData.get('type'),
                  description: formData.get('description'),
                  amount: parseFloat(formData.get('amount'))
                };
                if (payload.amount <= 0 || isNaN(payload.amount)) { toast.error('Valor inválido'); return; }
                handleSaveDed(payload);
              }}>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Categoria</label>
                  <select name="type" defaultValue={editingDed?.type || 'MEDICAL'} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary">
                    {Object.entries(DEDUCTION_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Descrição</label>
                  <input name="description" type="text" defaultValue={editingDed?.description || ''} placeholder="Ex: Plano de saúde — Jan/2024" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Valor (R$)</label>
                  <input name="amount" type="number" step="0.01" min="0.01" defaultValue={editingDed?.amount || ''} placeholder="0,00" required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <button type="submit" className="w-full mt-2 bg-primary text-white font-bold py-3.5 px-4 rounded-xl shadow-md hover:bg-indigo-700 transition-colors">
                  {editingDed ? 'Salvar Alterações' : 'Adicionar Dedução'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default IncomeTaxPage;
